import Styles from '@/style/abstracts/_shared.scss';

/**
 * Handles resize state on a given table
 * Not vue or grid dependent, it should work on any HTML table
 * Fast enough to be created/destroyed on each Vue render cycle
 */
export default class ResizeManager {
    MIN_COL_WIDTH = 90;

    dragStartX = null;

    leftCol = null;

    leftColWidth = null;

    table = null;

    cols = null;

    onResizeCallback = null;

    mouseDownHandler = this.onMouseDown.bind(this);

    mouseUpHandler = this.onMouseUp.bind(this);

    mouseMoveHandler = this.onMouseMove.bind(this);

    get columnList() {
        return Array.from(this.cols);
    }

    /**
     * Initializes the resize manager, adds event listeners, creates the
     * drag resize handlers, and applies initial widths
     * @param {HTMLTableElement} table HTML table to make resizeable
     * @param {Array} userSetWidths array of widths in pixels to be applied at init- these are
     *  values that a user has set and saved. If none are present, the table and columns are
     *  automatically sized based on content
     * @param {Array} defaultWidths array of widths to be applied at init with lower priority than
     *  userSetWidths. These are explicitly defined defaults meant to override automatic sizing
     *  (e.g. for button columns). If only some defaults are present, the rest of the columns
     *  will be automatically sized.
     * @param {Function} callback called on mouseup after user drags a resize handle
     */
    constructor(
        table,
        {userSetWidths = [], defaultWidths = []},
        callback = null,
    ) {
        if (!table) {
            throw new Error('No table provided');
        }
        this.table = table;
        this.table.classList.add('resizeable');
        const row = table.querySelector('tr');
        this.cols = row ? row.children : null;
        if (!this.cols) {
            throw new Error('Table is empty');
        }

        const widthSettingsPresent = userSetWidths.some(entry => entry);

        this.columnList.forEach((col, index) => {
            if (widthSettingsPresent) {
                // if table layout is fixed, we need a width fallback
                col.style.width = userSetWidths[index]
                || defaultWidths[index]
                || Styles.defaultColumnWidth;
            } else {
                // if table layout is auto, let columns size themselves
                col.style.width = defaultWidths[index] || null;
            }

            // adds resize handle+listener between each column
            const handle = ResizeManager.createResizeHandle();
            handle.addEventListener('mousedown', this.mouseDownHandler);
            col.appendChild(handle);
        });
        // adding this class sets table layout to fixed
        // this unlocks table width from the container
        if (widthSettingsPresent) {
            this.table.classList.add('resized');
        }

        // register event listeners and callbacks
        if (callback) {
            this.onResizeCallback = callback;
        }
        document.addEventListener('mousemove', this.mouseMoveHandler);
        document.addEventListener('mouseup', this.mouseUpHandler);
    }

    /**
     * Clean up event handlers
     * Make sure to call destroy() before releasing the reference to this instance
     */
    destroy() {
        document.removeEventListener('mousemove', this.mouseMoveHandler);
        document.removeEventListener('mouseup', this.mouseUpHandler);
        const handles = this.table.getElementsByClassName('resize-handle');
        Array.from(handles).forEach((handle) => {
            handle.remove();
        });
        this.table.classList.remove('resizeable');
    }

    getWidths() {
        const widths = [];
        this.columnList.forEach((col) => {
            widths.push(col.style.width || null);
        });
        return widths;
    }

    explicitlySetWidths() {
        this.columnList.forEach((col) => {
            const {width} = col.getBoundingClientRect();
            col.style.width = `${width}px`;
        });
        this.table.classList.add('resized');
    }

    resetWidths() {
        this.columnList.forEach((col) => {
            col.style.width = null;
        });
        this.table.classList.remove('resized');
        this.onResizeCallback([]);
    }

    onMouseDown(e) {
        // ignore all except left click
        if (e.which !== 1) {
            return;
        }
        // ignore columns with resize disabled
        if (!e.target.closest('.is-resizeable')) {
            return;
        }
        this.dragStartX = e.pageX;
        this.leftCol = e.target.parentElement;
        this.leftColWidth = this.leftCol.offsetWidth;

        this.explicitlySetWidths();

        // stop click propagation so that it
        // doesn't trigger a sort change on the column
        e.stopPropagation();
    }

    onMouseUp() {
        if (this.leftCol && this.onResizeCallback) {
            this.onResizeCallback(this.getWidths());
        }
        this.dragStartX = null;
        this.leftCol = null;
        this.leftColWidth = null;
    }

    onMouseMove(e) {
        if (this.leftCol) {
            const diffX = e.pageX - this.dragStartX;
            const newWidth = `${Math.max(this.leftColWidth + diffX, this.MIN_COL_WIDTH)}px`;
            this.leftCol.style.width = newWidth;
        }
    }

    static createResizeHandle() {
        const handle = document.createElement('div');
        handle.classList.add('resize-handle');
        handle.title = 'Click and drag to resize columns';
        return handle;
    }
}
